// src/pages/admin/LearnersListPage.jsx
import { useEffect, useRef, useState, useMemo } from 'react'
import {
  Plus, Loader, FileSpreadsheet, QrCode, User, UserCheck, Users, Mail, Phone,
  CircleUser, Edit, Trash2, Search, Send, Clock, Briefcase, Globe, Cake, Ellipsis, X
} from 'lucide-react'
import Select from 'react-select'
import * as XLSX from 'xlsx'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import Drawer from '@/layout/Drawer'
import ConfirmModal from '@/widgets/ConfirmModal'
import InviteModal from '@/widgets/InviteModal'
import InviteQRModal from '@/widgets/InviteQRModal'
import ImportPreview from '@/widgets/ImportPreview'
import ExcelImportModal from '@/widgets/ExcelImportModal'
import SavingModal from '@/widgets/SavingModal'
import DataTable from '@/widgets/DataTable'
import {
  getLearners, addLearner, bulkAddLearners, createLearnerInvite, resendInvite,
  getGroups, deleteLearner, updateLearner,
  getPrefixes, getCountries, getJobTitles
} from '@/api/kuizy'
import { formatDateTime, asArr } from '@/settings/master'

const emailRx = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const phoneRx = /^[0-9]{6,15}$/
const initForm = {
  id: '', group_ids: ['8af9145b-07fd-426b-aaf3-467579df77e4'], prefix_id: '', first_name: '', last_name: '', nick_name: '',
  job_title: '', birth_year: '', country: 'TH', phone_code: '+66',
  phone: '', email: '', username: '', password: ''
}

export default function LearnersListPage() {
  const uid = localStorage.getItem('userId') || '1'
  const nav = useNavigate()
  const [prefixes, setPrefixes] = useState([])
  const [countries, setCountries] = useState([])
  const [jobs, setJobs] = useState([])
  const [rows, setRows] = useState([])
  const [total, setTotal] = useState(0)
  const [groups, setGroups] = useState([])
  const [loading, setLoading] = useState(true)
  const [statusFilter, setStatus] = useState('all')
  const [groupFilter, setGFilter] = useState('')
  const [jobFilter, setJob] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(() => {
    const saved = localStorage.getItem("learners_per_page")
    return saved ? parseInt(saved) : 10
  })


  const [sortBy, setSortBy] = useState("created_at")
  const [sortDir, setSortDir] = useState("desc")
  const [drawer, setDrawer] = useState(false)
  const [editing, setEditing] = useState(false)
  const [form, setForm] = useState(initForm)
  const fileRef = useRef(null)
  const [excelInfo, setExcelInfo] = useState(null)
  const [inviteOpen, setInviteOpen] = useState(false)
  const [inviteLink, setInviteLink] = useState('')
  const [inviteLearnerInvite, setInviteLearnerInvite] = useState(null)
  const [importOpen, setImportOpen] = useState(false)
  const [alert, setAlert] = useState({ open: false, msg: '' })
  const [delItem, setDelItem] = useState(null)
  const [confirmRes, setConfirmRes] = useState({ open: false, learner: null })
  const [saving, setSaving] = useState(false)
  const [savingMessage, setSavingMessage] = useState('')
  const [reloadKey, setReloadKey] = useState(0)

  const bootRef = useRef(false)
  useEffect(() => {
    if (bootRef.current) return
    bootRef.current = true
    Promise.all([getPrefixes(), getCountries(), getGroups(), getJobTitles(99)]).then(([pf, ct, gp, jt]) => {
      setPrefixes(pf.data)
      setCountries(ct.data)
      setGroups(gp.data || [])
      setJobs(jt.data || [])
    })
  }, [])

  useEffect(() => {
    localStorage.setItem("learners_per_page", String(perPage))
  }, [perPage])

  useEffect(() => {
    setLoading(true)
    getLearners({
      status: statusFilter === 'all' ? undefined : statusFilter,
      group_id: groupFilter || undefined,
      job_title: jobFilter || undefined,
      searchText: search || undefined,
      page,
      per_page: perPage,
      sort_by: sortBy,
      sort_dir: sortDir
    })
      .then(r => {
        const d = r.data || {}
        setRows(d.rows || [])
        setTotal(d.total || 0)
      })
      .catch(() => setAlert({ open: true, msg: 'โหลดข้อมูลไม่สำเร็จ' }))
      .finally(() => setLoading(false))
  }, [statusFilter, groupFilter, jobFilter, search, page, perPage, sortBy, sortDir, reloadKey])

  const refresh = () => setReloadKey(k => k + 1)

  const phoneSanitized = form.phone?.replace(/^0+/, '')
  const isEmailOk = emailRx.test(form.email)


  const isPhoneOk = !phoneSanitized || phoneRx.test(phoneSanitized)
  const canSave = form.first_name && form.last_name && form.email && isEmailOk
  const phoneDisp = u => (u.phone_code || '') + (u.phone || '') || '-'

  const save = e => {
    e.preventDefault()
    if (!canSave) return
    const payload = { ...form, group_ids: asArr(form.group_ids), phone: phoneSanitized, created_by: uid }
    setSavingMessage(editing ? 'กำลังบันทึก...' : 'กำลังเพิ่มผู้เรียน...')
    setSaving(true)
      ; (editing ? updateLearner : addLearner)(payload)
        .then(() => { setDrawer(false); setForm(initForm); refresh() })
        .catch(() => setAlert({ open: true, msg: 'บันทึกไม่สำเร็จ' }))
        .finally(() => setSaving(false))
  }

  const doResend = async () => {
    setSavingMessage('กำลังส่งอีเมลเชิญอีกครั้ง...')
    setSaving(true)
    try { await resendInvite(confirmRes.learner.id); refresh() } finally { setSaving(false); setConfirmRes({ open: false, learner: null }) }
  }

  const confirmDelete = () =>
    deleteLearner(delItem.id, delItem.status === 3)
      .then(refresh)
      .catch(() => setAlert({ open: true, msg: 'ไม่สามารถลบได้' }))
      .finally(() => setDelItem(null))

  const handleGenerateInvite = async (groupId, quota, expire_hours) => {
    try {
      const res = await createLearnerInvite(uid, quota, expire_hours, groupId)
      setInviteLink(`${window.location.origin}/invite/${res.data.token}`)
      setInviteLearnerInvite(res.data)
      setInviteOpen(false)
    } catch { setAlert({ open: true, msg: 'สร้างลิงก์ไม่สำเร็จ' }) }
  }

  const statusOptions = [
    { value: 'all', label: 'ทั้งหมด' },
    { value: '1', label: 'Active' },
    { value: '0', label: 'Pending' },
    { value: '2', label: 'Refused' },
    { value: '3', label: 'Trash' }
  ]
  const groupOptions = [{ value: '', label: 'ทั้งหมด' }, ...groups.map(g => ({ value: String(g.id), label: g.name }))]

  const jobOptions = [{ value: '', label: 'ทั้งหมด' }, ...jobs.map(j => ({ value: j.job_title, label: `${j.job_title} (${j.cnt})` }))]

  const prefixName = id => prefixes.find(p => p.id === id)?.name_th || null

  const columns = useMemo(() => [
    {
      key: "__no",
      title: "#",
      align: "left",
      sortable: false,
      render: (_, idx) => ((page - 1) * perPage) + idx + 1
    },
    {
      key: "full_name",
      title: "ชื่อ-สกุล",
      sortable: true,
      sortKey: "first_name",
      render: (u) => (<span>{prefixName(u.prefix_id)} {u.first_name} {u.last_name}{u.nick_name ? ` (${u.nick_name})` : ""}</span>)
    },
    {
      key: "job_title",
      title: "ตำแหน่ง",
      sortable: true,
      render: (u) => u.job_title || "-"
    },
    {
      key: "email",
      title: "อีเมล",
      sortable: true
    },
    {
      key: "phone",
      title: "โทรศัพท์",
      align: "center",
      sortable: false,
      render: (u) => (u.phone_code || '') + (u.phone || '') || '-'
    },
    {
      key: "group_names",
      title: "กลุ่มผู้เรียน",
      align: "center",
      sortable: false,
      render: (u) => u.group_names && u.group_names.length ? (Array.isArray(u.group_names) ? u.group_names.join(', ') : u.group_names) : 'Member'
    },
    {
      key: "status",
      title: "สถานะ",
      align: "center",
      sortable: true,
      render: (u) => (
        u.status === 1 ? <span className="inline-flex items-center gap-1 bg-green-50 text-green-700 px-2 py-[2px] rounded-full text-[10px]"><UserCheck size={10} />active</span> :
          u.status === 0 ? <span className="inline-flex items-center gap-1 bg-yellow-50 text-yellow-700 px-2 py-[2px] rounded-full text-[10px]"><Clock size={10} />pending</span> :
            u.status === 2 ? <span className="inline-flex items-center gap-1 bg-orange-50 text-orange-700 px-2 py-[2px] rounded-full text-[10px]"><X size={10} />refused</span> :
              <span className="inline-flex items-center gap-1 bg-red-50 text-red-700 px-2 py-[2px] rounded-full text-[10px]"><Trash2 size={10} />trash</span>
      )
    },
    {
      key: "created_at",
      title: "สร้างเมื่อ",
      align: "right",
      sortable: true,
      render: (u) => formatDateTime(u.created_at)
    },
    {
      key: "__actions",
      title: "จัดการ",
      align: "right",
      pinEnd: true,
      sortable: false,
      render: (u) => (
        <div className="space-x-2">
          {u.status === 0 && <button title="ส่งอีเมลเชิญอีกครั้ง" onClick={() => setConfirmRes({ open: true, learner: u })} className="text-secondary hover:text-secondary/80"><Send size={16} /></button>}
          <button title="แก้ไข" onClick={() => { setEditing(true); setForm({ id: u.id, group_ids: asArr(u.group_ids), prefix_id: u.prefix_id, first_name: u.first_name, last_name: u.last_name, nick_name: u.nick_name || '', job_title: u.job_title || '', birth_year: u.birth_year || '', country: u.country || 'TH', phone_code: u.phone_code || '', phone: u.phone || '', email: u.email, username: u.username, password: '' }); setDrawer(true) }} className="text-primary hover:text-primary/80"><Edit size={16} /></button>
          <button title="ลบ" onClick={() => setDelItem(u)} className="text-red-600 hover:text-red-500"><Trash2 size={16} /></button>
        </div>
      )
    }
  ], [page, perPage, prefixes])

  return (
    <motion.section className="flex flex-col gap-6 p-4 sm:p-6" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: 0.05 }} className="flex flex-wrap gap-3 items-center justify-between">
        <h1 className="flex items-center gap-2 text-lg font-semibold cursor-pointer" onClick={refresh}><User size={20} /> จัดการผู้เรียน ({total})</h1>
        <div className="flex flex-wrap gap-2">
          <button className="inline-flex items-center gap-1 bg-primary hover:bg-secondary text-white rounded-lg px-3 py-2 text-sm" onClick={() => { setEditing(false); setForm(initForm); setDrawer(true) }}><Plus size={16} /> เชิญผู้เรียนใหม่</button>
          <button className="inline-flex items-center gap-1 bg-primary hover:bg-secondary text-white rounded-lg px-3 py-2 text-sm" onClick={() => setImportOpen(true)}><FileSpreadsheet size={16} /> นำเข้าไฟล์</button>
          <button className="inline-flex items-center gap-1 bg-primary hover:bg-secondary text-white rounded-lg px-3 py-2 text-sm" onClick={() => setInviteOpen(true)}><QrCode size={16} /> เชิญผ่านลิงก์ / QR</button>
          <input hidden type="file" ref={fileRef} accept=".xlsx,.xls" onChange={async () => {
            const f = fileRef.current?.files?.[0]
            if (!f) return
            const wb = XLSX.read(await f.arrayBuffer(), { type: 'array' })
            const arr = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { defval: '' })
            setExcelInfo({ file: f, name: f.name, rows: arr })
            fileRef.current.value = ''
          }} />
        </div>
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: 0.1 }} className="bg-white rounded-xl shadow-md p-4 flex flex-wrap items-end gap-4">
        <div className="flex flex-col gap-1 w-48"><label className="flex items-center gap-1 text-sm"><UserCheck size={16} /> สถานะ</label><Select classNamePrefix="rs" options={statusOptions} value={statusOptions.find(o => o.value === statusFilter)} onChange={o => { setStatus(o.value); setPage(1) }} /></div>
        <div className="flex flex-col gap-1 w-56"><label className="flex items-center gap-1 text-sm"><Users size={16} /> กลุ่มผู้เรียน</label><Select classNamePrefix="rs" options={groupOptions} value={groupOptions.find(o => o.value === groupFilter)} onChange={o => { setGFilter(o.value); setPage(1) }} /></div>
        <div className="flex flex-col gap-1 w-56"><label className="flex items-center gap-1 text-sm"><Briefcase size={16} /> ตำแหน่งงาน</label><Select classNamePrefix="rs" options={jobOptions} value={jobOptions.find(o => o.value === jobFilter)} onChange={o => { setJob(o.value); setPage(1) }} /></div>
        <div className="flex flex-col gap-1 flex-1 min-w-[200px]">
          <label className="flex items-center gap-1 text-sm"><Search size={16} /> ค้นหา</label>
          <div className="relative">
            <input
              placeholder="ค้นหาชื่อ / อีเมล / โทรศัพท์"
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { setSearch(searchInput); setPage(1) } }}
              className="border border-gray-300 rounded-lg px-10 py-2 w-full text-sm focus:ring-2 focus:ring-primary"
            />
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          </div>
        </div>
        <button onClick={() => { setSearch(searchInput); setPage(1) }} className="inline-flex items-center gap-1 bg-secondary hover:bg-primary text-white rounded-lg px-3 py-2 text-sm">
          <Search size={16} /> ค้นหา
        </button>
      </motion.div>

      <DataTable
        columns={columns}
        rows={rows}
        loading={loading}
        total={total}
        page={page}
        perPage={perPage}
        sortBy={sortBy}
        sortDir={sortDir}
        onPageChange={setPage}
        onPerPageChange={(n) => { setPerPage(n); setPage(1) }}
        onSortChange={(key, dir) => { setSortBy(key); setSortDir(dir); setPage(1) }}
      />

      <Drawer open={drawer} title={editing ? 'แก้ไขผู้เรียน' : 'เชิญผู้เรียนใหม่'} onClose={() => setDrawer(false)}>
        <form onSubmit={save} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1"><label className="flex items-center gap-1 text-sm"><Users size={16} /> กลุ่มผู้เรียน</label><div className="relative">
            <Select
              isMulti
              classNamePrefix="rs"
              options={groupOptions.filter(o => o.value !== '')}
              value={groupOptions.filter(o => o.value !== '').filter(o => form.group_ids.includes(o.value))}
              onChange={o => setForm({ ...form, group_ids: o.map(x => x.value) })}
            />
          </div></div>
          <h3 className="text/base font-semibold mt-2">ข้อมูลพื้นฐาน</h3>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="flex flex-col gap-1"><label className="flex items-center gap-1 text-sm"><Ellipsis size={14} /> คำนำหน้า</label><Select classNamePrefix="rs" options={prefixes.map(p => ({ value: p.id, label: `${p.name_th} (${p.name_en})` }))} value={prefixes.map(p => ({ value: p.id, label: `${p.name_th} (${p.name_en})` })).find(o => o.value === form.prefix_id)} onChange={o => setForm({ ...form, prefix_id: o.value })} /></div>
            <div className="flex flex-col gap-1"><label className="flex items-center gap-1 text-sm"><Briefcase size={14} /> ตำแหน่งงาน</label><input value={form.job_title} onChange={e => setForm({ ...form, job_title: e.target.value })} className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary" /></div>
            <div className="flex flex-col gap-1"><label className="flex items-center gap-1 text-sm"><User size={14} /> ชื่อ <span className="text-red-500 font-bold">*</span></label><input value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full focus:ring-2 focus:ring-primary" /></div>
            <div className="flex flex-col gap-1"><label className="flex items-center gap-1 text-sm"><CircleUser size={14} /> นามสกุล <span className="text-red-500 font-bold">*</span></label><input value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full focus:ring-2 focus:ring-primary" /></div>
            <div className="flex flex-col gap-1"><label className="flex items-center gap-1 text-sm"><User size={14} /> ชื่อเล่น</label><input value={form.nick_name} onChange={e => setForm({ ...form, nick_name: e.target.value })} className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary" /></div>
            <div className="flex flex-col gap-1"><label className="flex items-center gap-1 text-sm"><Cake size={14} /> ปีเกิด </label><input value={form.birth_year} onChange={e => setForm({ ...form, birth_year: e.target.value.replace(/\D/g, '') })} className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary" /></div>
            <div className="flex flex-col gap-1 sm:col-span-2"><label className="flex items-center gap-1 text-sm"><Globe size={14} /> ประเทศ *</label><Select classNamePrefix="rs" options={countries.map(c => ({ value: c.iso2, label: `${c.name_en} (${c.phone_code})` }))} value={countries.map(c => ({ value: c.iso2, label: `${c.name_en} (${c.phone_code})` })).find(o => o.value === form.country)} onChange={o => setForm({ ...form, country: o.value, phone_code: countries.find(c => c.iso2 === o.value)?.phone_code || '' })} /></div>
            <div className="flex flex-col gap-1 sm:col-span-2"><label className="flex items-center gap-1 text-sm"><Phone size={14} /> โทรศัพท์</label><div className="flex gap-2"><input value={form.phone_code} onChange={e => setForm({ ...form, phone_code: e.target.value })} className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-24 focus:ring-2 focus:ring-primary text-center" /><input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value.replace(/\D/g, '') })} placeholder='ใส่่เฉพาะตัวเลข 10 หลัก' className="border border-gray-300 rounded-lg px-3 py-2 text-sm flex-1 focus:ring-2 focus:ring-primary" /></div>{!isPhoneOk && <small className="text-red-600 text-xs mt-1">เบอร์โทรไม่ถูกต้อง</small>}</div>
            <div className="flex flex-col gap-1 sm:col-span-2"><label className="flex items-center gap-1 text-sm"><Mail size={14} /> อีเมล <span className="text-red-500 font-bold">*</span></label><input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full focus:ring-2 focus:ring-primary" />{!isEmailOk && <small className="text-red-600 text-xs mt-1">รูปแบบอีเมลไม่ถูกต้อง</small>}</div>
          </div>
          <button disabled={saving || !canSave} className="bg-primary hover:bg-secondary disabled:opacity-50 text-white rounded-lg px-4 py-2">{saving ? <Loader size={16} className="animate-spin inline" /> : <span>{editing ? 'บันทึกการแก้ไข' : 'ส่งอีเมลเชิญ'}</span>}</button>
        </form>
      </Drawer>

      <AnimatePresence>
        <SavingModal open={saving} message={savingMessage} />
        {inviteOpen && <InviteModal groups={groups} onGenerate={handleGenerateInvite} onClose={() => setInviteOpen(false)} />}
        {inviteLink && <InviteQRModal link={inviteLink} learnerInvite={inviteLearnerInvite} onClose={() => setInviteLink('')} />}
        {importOpen && <ExcelImportModal open={importOpen} onUpload={({ file }) => { setExcelInfo({ file }); setImportOpen(false) }} onClose={() => setImportOpen(false)} />}
        {excelInfo && <ImportPreview info={excelInfo} groups={groups} onConfirm={refresh} onCancel={() => setExcelInfo(null)} />}
        {confirmRes.open && <ConfirmModal type="confirm" title="ส่งอีเมลเชิญซ้ำ" message={`ส่งอีเมลเชิญให้ "${confirmRes.learner.first_name} ${confirmRes.learner.last_name}" อีกครั้ง ?`} onConfirm={() => doResend()} onCancel={() => setConfirmRes({ open: false, learner: null })} />}
        {delItem && <ConfirmModal type="confirm" title={delItem.status === 3 ? 'ลบถาวร' : 'ย้ายไปถังขยะ'} message={`${delItem.status === 3 ? 'ลบถาวร' : 'ย้ายไปถังขยะ'} "${delItem.first_name} ${delItem.last_name}" ?`} onConfirm={confirmDelete} onCancel={() => setDelItem(null)} />}
        {alert.open && <ConfirmModal type="alert" title="แจ้งเตือน" message={alert.msg} onClose={() => setAlert({ open: false, msg: '' })} />}
      </AnimatePresence>
    </motion.section>
  )
}
